import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef
} from "react";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import { PeopleView } from "@/entities/people/types";
import { usePeopleTable } from "@/services/hooks/entities/person/usePeopleTable";
import PeopleTable from "@/components/layouts/People/PeopleTable";
import {
  EmptyView,
  ViewDataConfiguration,
  ViewType
} from "@/entities/views/types";
import { ColumnData } from "@/entities/dataTables/types";
import {
  saveConfigToTempView,
  getTempView
} from "@/entities/dataTables/helpers";
import { getFullName } from "@/entities/people/helpers";
import AddPeopleModal from "@/components/layouts/AddPeopleModal/AddPeopleModal";
import {
  useDeals,
  useResetDeal,
  useUpdateDealMutation
} from "@/pages/deals/hooks";
import EmptyState from "./components/EmptyState";
import PeopleCardsGrid from "./components/PeopleCardsGrid";
import NewPersonButton from "./components/NewPersonButton";
import { isEmpty as lodashIsEmpty, isEqual } from "lodash";
import { ComputedFields } from "@/services/api/types";
import { usePeopleSelection } from "@/services/hooks/entities/person/usePeopleSelection";
import PeopleBulkActionPopover from "./components/PeopleBulkActionPopover";
import ConfirmationModal from "@/components/layouts/ConfirmationModal";
import toast from "react-hot-toast";
import ToolbarActions from "@/components/controls/ToolbarActions/ToolbarActions";
import Tabs from "@/components/controls/Tabs";
import { Cards, Table, Account, Partners } from "@dreamhub/designsystem/icons";
import { DEAL_PEOPLE_TABLE_ID } from "@/components/layouts/PeopleTab/constants";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { getEnumTKey } from "@/utils/strings";
import PartnerContactsTable from "./components/PartnerContactsTable";
import { useEnablePartnerships } from "@/pages/partners/hooks";

export { DEAL_PEOPLE_TABLE_ID };

const TRANSLATION_PREFIX =
  "detailedView.peopleTab.bulkActions.removeConfirmationModal";

enum PeopleSource {
  COMPANY = "company",
  PARTNER = "partner"
}

interface Props {
  entityId: string;
  companyId: string;
  dealId: string;
}

const DealPeopleTab: React.FC<Props> = ({ entityId, companyId, dealId }) => {
  const { t } = useTranslation();
  const entityName = t(
    getEnumTKey("entities", "names", EntityType, EntityType.DEAL)
  ).toLowerCase();
  const resetDeal = useResetDeal(dealId);
  const updateDeal = useUpdateDealMutation();
  // Mutation objects get a new identity on every state change — keep it in a
  // ref so the update callbacks stay referentially stable.
  const updateDealRef = useRef(updateDeal);
  useEffect(() => {
    updateDealRef.current = updateDeal;
  }, [updateDeal]);

  const hiddenColumns = ["companyId", "companyName", "id"];

  const [tabValue, setTabValue] = useState(PeopleView.TABLE.toString());
  const enablePartnerships = useEnablePartnerships();
  const [peopleSource, setPeopleSource] = useState<PeopleSource>(
    PeopleSource.COMPANY
  );
  // Partner contacts only exist for accounts that have partnerships set up, so
  // when it is off the Company/Partner toggle is hidden and the view is pinned
  // to company people.
  const isPartnerSource =
    enablePartnerships && peopleSource === PeopleSource.PARTNER;
  const [isAddPeopleModalOpen, setIsAddPeopleModalOpen] = useState(false);
  const [isRemoveConfirmationOpen, setIsRemoveConfirmationOpen] =
    useState(false);
  const [selectedView] = useState<ViewDataConfiguration>(() => ({
    ...EmptyView(ViewType.PEOPLE_TABLE),
    id: DEAL_PEOPLE_TABLE_ID,
    isOpen: true
  }));

  const [columnsState, setColumnsState] = useState<ColumnData[]>(() => {
    const tempView = getTempView(selectedView);
    return tempView?.config?.columns ?? [];
  });

  const handleColumnsChange = useCallback(
    (updatedColumns?: ColumnData[]) => {
      if (updatedColumns) {
        setColumnsState(updatedColumns);
        saveConfigToTempView(selectedView, {
          columns: updatedColumns
        });
      }
    },
    [selectedView]
  );

  useEffect(() => {
    const existingView = getTempView(selectedView);
    if (!existingView) {
      saveConfigToTempView(selectedView, {
        filters: {},
        columns: []
      });
    }
  }, []);

  const {
    entities: [deal],
    isSuccess: dealIsSuccess
  } = useDeals([dealId], {
    enabled: !!dealId,
    computed_fields: ComputedFields.ALL
  });

  const allDealPeople = deal?.allDealPeople ?? [];

  const partnerContactIds = useMemo(() => {
    const all = deal?.allDealPartnerContacts ?? [];
    if (all.length > 0) {
      return [...new Set(all)];
    }
    const manual = deal?.manuallyAddedPartnerContacts ?? [];
    const automatic = deal?.automaticallyAddedPartnerContacts ?? [];
    return [...new Set([...manual, ...automatic])];
  }, [
    deal?.allDealPartnerContacts,
    deal?.manuallyAddedPartnerContacts,
    deal?.automaticallyAddedPartnerContacts
  ]);

  const initialFilters = useMemo(() => {
    return allDealPeople.length > 0 ? { id: { in: allDealPeople } } : {};
  }, [allDealPeople]);

  const {
    people,
    reportsToDataMap,
    sorting,
    onSortingChange,
    isLoading: peopleLoading,
    isFetchingNextPage,
    hasNextPage,
    searchTerm,
    setSearchTerm,
    filters,
    setFilters,
    peopleFilterOptions,
    peopleSortByOptions,
    handleLoadMore,
    resetPage,
    isError,
    refetch
  } = usePeopleTable({
    hiddenColumns,
    initialFilters,
    enabled: dealIsSuccess && allDealPeople.length > 0 && !isPartnerSource
  });

  const isLoading = !dealIsSuccess || peopleLoading;

  const {
    selectedPeople,
    selectAll,
    selectedPeopleData,
    handlePersonSelect,
    handleSelectAll,
    clearSelection
  } = usePeopleSelection(people);

  const getReportsToPersonName = (personId: string | null | undefined) => {
    if (lodashIsEmpty(personId)) {
      return "";
    }
    const person = reportsToDataMap[personId as string];
    if (!person) return t("notFound");
    return getFullName(person);
  };

  const handleRemoveFromDeal = useCallback(() => {
    setIsRemoveConfirmationOpen(true);
  }, []);

  const handleSelectPartner = useCallback(
    (selectedPartnerId: string): Promise<void> => {
      return new Promise((resolve, reject) => {
        updateDealRef.current.mutate(
          {
            pathParams: { dealId: entityId },
            data: {
              name: deal?.name,
              partnerId: selectedPartnerId
            }
          },
          {
            onSuccess: () => {
              resetDeal();
              resolve();
            },
            onError: (error) => {
              toast.error(t("deals.dealView.updateError"));
              reject(error);
            }
          }
        );
      });
    },
    [entityId, deal?.name, resetDeal, t]
  );

  const handleConfirmRemoveFromDeal = useCallback(async () => {
    const selectedPeopleIds = selectedPeopleData.map((person) => person.id);
    const newPeopleArray =
      deal?.manuallyAddedPeople?.filter(
        (personId) => !selectedPeopleIds.includes(personId)
      ) || [];
    const removablePeople = selectedPeopleIds.filter((id) =>
      deal?.manuallyAddedPeople?.includes(id)
    );
    updateDealRef.current.mutate(
      {
        pathParams: {
          dealId: entityId
        },
        data: {
          name: deal?.name,
          manuallyAddedPeople: newPeopleArray
        }
      },
      {
        onSuccess: () => {
          resetDeal();
          setTimeout(() => {
            resetPage?.();
          }, 100);
          setIsRemoveConfirmationOpen(false);
          clearSelection();
          toast.success(
            t(`${TRANSLATION_PREFIX}.success`, {
              count: removablePeople.length,
              entityName
            })
          );
        }
      }
    );
  }, [
    selectedPeopleData,
    clearSelection,
    deal?.manuallyAddedPeople,
    dealId,
    entityId,
    deal?.name,
    resetPage,
    entityName,
    t
  ]);

  const handleCloseBulkActions = useCallback(() => {
    clearSelection();
  }, [clearSelection]);

  const sourceOptions = useMemo(
    () => ({
      [PeopleSource.COMPANY]: (
        <div className="flex gap-2 items-center [&>svg]:w-5 [&>svg]:h-5">
          <Account />
          <Text as="span">{t("detailedView.peopleTab.company")}</Text>
        </div>
      ),
      [PeopleSource.PARTNER]: (
        <div className="flex gap-2 items-center [&>svg]:w-5 [&>svg]:h-5">
          <Partners />
          <Text as="span">{t("detailedView.peopleTab.partner")}</Text>
        </div>
      )
    }),
    [t]
  );

  const showAsOptions = useMemo(
    () => [
      { value: PeopleView.TABLE.toString(), label: <Table /> },
      { value: PeopleView.CARDS.toString(), label: <Cards /> }
    ],
    []
  );

  const handleSourceChange = useCallback(
    (value: string) => {
      const nextSource = value as PeopleSource;
      if (nextSource !== peopleSource) {
        clearSelection();
      }
      setPeopleSource(nextSource);
    },
    [peopleSource, clearSelection]
  );

  const renderContent = () => {
    return (
      <div className="flex flex-col w-full h-full overflow-hidden">
        <div>
          <ToolbarActions
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            filters={filters}
            setFilters={setFilters}
            filterOptions={peopleFilterOptions}
            sorting={sorting}
            onSortingChange={onSortingChange}
            sortByOptions={peopleSortByOptions}
            defaultSortByValue="fullName"
            showTableColumns={false}
            showDisplayButton={!isPartnerSource}
            showSearch={!isPartnerSource}
            showFilters={!isPartnerSource}
            showAsValue={tabValue}
            onShowAsChange={setTabValue}
            showAsOptions={showAsOptions}
            onColumnsChange={handleColumnsChange}
            tableId={DEAL_PEOPLE_TABLE_ID}
            className="p-4 border-none"
            baseFilters={initialFilters}
            leftActions={
              enablePartnerships ? (
                <Tabs
                  options={sourceOptions}
                  value={peopleSource}
                  valueSelected={handleSourceChange}
                  containerClassName="bg-surface-field-default border-[0.5px]"
                  className="px-2 py-1"
                />
              ) : undefined
            }
            rightActions={
              <NewPersonButton
                onClick={() => setIsAddPeopleModalOpen(true)}
                isDealMode={true}
                text={
                  isPartnerSource
                    ? t("detailedView.peopleTab.addPartnerContact")
                    : undefined
                }
              />
            }
          />
        </div>
        <div className="flex-1 overflow-hidden">
          {isPartnerSource ? (
            <PartnerContactsTable
              partnerContactIds={partnerContactIds}
              entityType={EntityType.DEAL}
              onAddPartnerContact={() => setIsAddPeopleModalOpen(true)}
              className="h-[66vh]"
            />
          ) : allDealPeople.length === 0 ? (
            <EmptyState onAddPeople={() => setIsAddPeopleModalOpen(true)} />
          ) : tabValue === PeopleView.TABLE.toString() ? (
            <PeopleTable
              people={people}
              sorting={sorting}
              onSortingChange={onSortingChange}
              isLoading={isLoading}
              isFetchingNextPage={isFetchingNextPage}
              hasNextPage={hasNextPage}
              onLoadMore={handleLoadMore}
              hiddenColumns={hiddenColumns}
              tableId={DEAL_PEOPLE_TABLE_ID}
              selectedPeople={selectedPeople}
              selectAll={selectAll}
              onPersonSelect={handlePersonSelect}
              onSelectAll={handleSelectAll}
              selectedView={selectedView}
              columnsState={columnsState}
              className="h-[66vh]"
              onColumnsChange={handleColumnsChange}
              hasActiveFilters={!isEqual(filters, initialFilters)}
              onClearFilters={() => setFilters(initialFilters)}
              onClearSearch={() => setSearchTerm("")}
              isError={isError}
              onRetry={refetch}
            />
          ) : (
            <PeopleCardsGrid
              people={people}
              getReportsToPersonName={getReportsToPersonName}
            />
          )}
        </div>
      </div>
    );
  };

  const getRemovalTitle = () => {
    const selectedCount = selectedPeopleData.length;
    const selectedPeopleIds = selectedPeopleData.map((person) => person.id);
    const removablePeople = selectedPeopleIds.filter((id) =>
      deal?.manuallyAddedPeople?.includes(id)
    );
    const nonRemovablePeople = selectedPeopleIds.filter(
      (id) => !deal?.manuallyAddedPeople?.includes(id)
    );

    if (nonRemovablePeople.length > 0 && removablePeople.length > 0) {
      return t(`${TRANSLATION_PREFIX}.partialTitle`);
    }

    if (selectedCount === 1) {
      const personName = getFullName(selectedPeopleData[0]);
      return t(`${TRANSLATION_PREFIX}.singleTitle`, {
        personName,
        entityName
      });
    } else {
      return t(`${TRANSLATION_PREFIX}.multiTitle`, { entityName });
    }
  };

  const getRemovalConfirmationText = () => {
    const companyName = deal?.companyName || "Company";
    const selectedCount = selectedPeopleData.length;
    const selectedPeopleIds = selectedPeopleData.map((person) => person.id);
    const removablePeople = selectedPeopleIds.filter((id) =>
      deal?.manuallyAddedPeople?.includes(id)
    );
    const nonRemovablePeople = selectedPeopleIds.filter(
      (id) => !deal?.manuallyAddedPeople?.includes(id)
    );

    if (nonRemovablePeople.length > 0 && removablePeople.length > 0) {
      return t(`${TRANSLATION_PREFIX}.partialText`, {
        removableCount: removablePeople.length,
        selectedCount,
        companyName,
        entityName
      });
    }

    if (selectedCount === 1) {
      const personName = getFullName(selectedPeopleData[0]);
      return t(`${TRANSLATION_PREFIX}.singleText`, {
        personName,
        companyName,
        entityName
      });
    } else {
      return t(`${TRANSLATION_PREFIX}.multiText`, {
        selectedCount,
        companyName,
        entityName
      });
    }
  };

  return (
    <>
      {renderContent()}
      {selectedPeople.size > 0 && (
        <PeopleBulkActionPopover
          selectedPeople={selectedPeopleData}
          onRemoveFromDeal={handleRemoveFromDeal}
          onClose={handleCloseBulkActions}
          manuallyAddedPeople={deal?.manuallyAddedPeople || undefined}
          automaticallyAddedPeople={deal?.automaticallyAddedPeople || undefined}
        />
      )}
      {isAddPeopleModalOpen && (
        <AddPeopleModal
          isOpen={true}
          setIsOpen={setIsAddPeopleModalOpen}
          entityId={entityId || ""}
          companyId={companyId}
          resetPage={resetPage}
          allDealPeople={deal?.allDealPeople}
          mode={isPartnerSource ? "partnerContacts" : "people"}
          partnerId={deal?.partnerId}
          onSelectPartner={handleSelectPartner}
          addedPartnerContacts={partnerContactIds}
        />
      )}
      <ConfirmationModal
        isOpen={isRemoveConfirmationOpen}
        setIsOpen={setIsRemoveConfirmationOpen}
        title={getRemovalTitle()}
        confirmationText={getRemovalConfirmationText()}
        handleSubmit={handleConfirmRemoveFromDeal}
        submitButtonText={t("remove")}
        cancelButtonText={t("cancel")}
        isPrimaryButton={false}
      />
    </>
  );
};

export default DealPeopleTab;
